import { Field as HeadlessField } from '@flywire/headlessui-tailwindcss';
import classNames from 'classnames';
import { VisuallyHidden } from 'components/ui/VisuallyHidden/VisuallyHidden';
import type { Field } from 'models/field';
import { useTranslations } from 'utils/translations/useTranslations';

const RadioField = ({
  field,
  handleChange,
  handleBlur,
}: {
  field: Field;
  handleChange: (name: string, value: string) => void;
  handleBlur: (name: string) => void;
}) => {
  const i18n = useTranslations();
  const selectedValue = field.defaultValue || '';
  const options = Array.isArray(field.value) ? field.value : [];

  const onChange = (value: string) => {
    handleChange(field.id, value);
  };

  return (
    <div className="flex flex-col gap-1.5 w-full">
      <div
        className={classNames(
          'flex divide-x divide-gray-500 rounded box-border border border-gray-500 max-h-12',
          field.error && 'border-red-600 border-2',
        )}
      >
        {options.map(option => (
          <HeadlessField
            key={`${option.value}`}
            id={field.id}
            className="flex-1 items-start px-4 py-3.5"
          >
            <div className="flex cursor-pointer items-center gap-4">
              <HeadlessField.Radio
                checked={option.value === selectedValue}
                value={option.value}
                onChange={() => onChange(option.value)}
                required={!!field.required}
                disabled={!!field.readOnly}
                onBlur={() => handleBlur(field.id)}
                name={field.id}
              />
              <HeadlessField.Label
                htmlFor={option.value}
                className="cursor-pointer text-sm font-normal text-gray-700 m-0"
              >
                {option.label}
              </HeadlessField.Label>
            </div>
          </HeadlessField>
        ))}
      </div>
      {field.hint && (
        <HeadlessField.Hint
          className="block mb-0"
          data-testid={`${field.id}-hint`}
        >
          {field.hint}
        </HeadlessField.Hint>
      )}
      {field.error && typeof field.error === 'string' && (
        <HeadlessField.Error className="mb-0" data-testid={`${field.id}-error`}>
          <VisuallyHidden as="span">{field.label}</VisuallyHidden>
          {i18n.t(field.error)}
        </HeadlessField.Error>
      )}
    </div>
  );
};

RadioField.displayName = 'RadioField';

export { RadioField };
